/* eslint-disable jsx-a11y/heading-has-content */
// TODO: Probably should be moved to widgets - Maksym
import styles from "./ChatMessage.module.css";
import ReactMarkdown, { Components } from "react-markdown";
import rehypeRaw from "rehype-raw";
import { memo, useEffect, useState } from "react";
import { ChatMessage as ChatMessageType } from "@/zapi-frontend/stores/chatStore";
import { GlobeSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { parseMessageContent } from "./lib/parseMessageContent";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import {
  AiMessage,
  ErrorBox,
  NoteBox,
  ProgressStep,
  PrereqChapterList,
  StatusRow,
  StepList,
  TypingDots,
  UserMessage
} from "@/zapi-frontend/shared/ui/chat";
import { SparkleIcon } from "@libs/zapi-ui/assets";
import { markdownComponents, MessageMarkdown } from "./MessageMarkdown";

export type ParsedContent = {
  type:
    | "answer"
    | "websearch"
    | "error"
    | "warning"
    | "loading"
    | "updateChapter"
    | "answerAndUpdate"
    | "multiAction"
    | "agent"
    | "prereqSummary";
  content: string;
  notes?: string[];
  agentType?: string;
  chapterName?: string;
  chapters?: string[];
  actions?: Array<{
    type: string;
    content: string;
    notes?: string[];
    agentType?: string;
  }>;
};

type TChatMessageProps = {
  message: ChatMessageType;
  isLoading?: boolean;
  progressMessage?: string | null;
  webLinkEnabled?: boolean;
  messageAttachmentsEnabled?: boolean;
};

const stepNoteMarkdownComponents: Components = {
  ...markdownComponents,
  p: ({ node: _node, ...props }) => <p {...props} className="mb-0" />
};

const inlineMarkdownComponents: Components = {
  ...markdownComponents,
  p: ({ node: _node, ...props }) => <span {...props} />
};

function InlineMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown rehypePlugins={[rehypeRaw]} components={inlineMarkdownComponents}>
      {content || ""}
    </ReactMarkdown>
  );
}

function StepNoteMarkdown({ content }: { content: string }) {
  return (
    <div className="w-full text-[14px] leading-[22px] text-innoveraZinc-900">
      <ReactMarkdown rehypePlugins={[rehypeRaw]} components={stepNoteMarkdownComponents}>
        {content || ""}
      </ReactMarkdown>
    </div>
  );
}

export const ChatMessage = memo(function ChatMessage({
  message,
  isLoading,
  progressMessage,
  webLinkEnabled: _webLinkEnabled = false,
  messageAttachmentsEnabled: _messageAttachmentsEnabled = false
}: TChatMessageProps) {
  const { isLabs } = useMeStore();
  const [parsedContent, setParsedContent] = useState<ParsedContent>({
    type: "loading",
    content: "Loading..."
  });
  const [parseError, setParseError] = useState(false);

  useEffect(() => {
    if (message.role === "user") {
      setParsedContent({ type: "answer", content: message.content });
      setParseError(false);
      return;
    }

    if (isLoading) {
      setParsedContent({ type: "loading", content: "Loading..." });
      setParseError(false);
      return;
    }

    try {
      const parsed = parseMessageContent(message.content, !!isLabs);
      setParsedContent(parsed);
      setParseError(false);
    } catch (error) {
      console.error("Critical error parsing message:", error);
      setParsedContent({
        type: "error",
        content: "There was a problem displaying this message. It may contain invalid formatting."
      });
      setParseError(true);
    }
  }, [message, isLoading, isLabs]);

  if (message.role === "user") {
    return (
      <div className={`${styles.animatefadeIn} w-full`}>
        <UserMessage className="w-full">
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
        </UserMessage>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`${styles.animatefadeIn} w-full`}>
        <AiMessage className="w-full">
          {progressMessage ? (
            <>
              <FigmaTypography variant="body-2" color="zinc900" className="w-full">Working on it...</FigmaTypography>
              <StatusRow icon={<MagnifyingGlass size={16} />} muted>
                {progressMessage}
              </StatusRow>
            </>
          ) : (
            <TypingDots />
          )}
        </AiMessage>
      </div>
    );
  }

  if (parseError) {
    return (
      <div className={`${styles.animatefadeIn} w-full`}>
        <AiMessage className="w-full" state="error">
          <ErrorBox>{parsedContent.content}</ErrorBox>
        </AiMessage>
      </div>
    );
  }

  const renderByType = () => {
    switch (parsedContent.type) {
      case "websearch":
        return (
          <AiMessage className="w-full">
            <StatusRow icon={<GlobeSimple size={16} />}>Web research</StatusRow>
            <MessageMarkdown content={parsedContent.content} />
          </AiMessage>
        );
      case "error":
        return (
          <AiMessage className="w-full" state="error">
            <ErrorBox>{parsedContent.content}</ErrorBox>
          </AiMessage>
        );
      case "warning":
      case "multiAction":
        return (
          <AiMessage className="w-full">
            <NoteBox>
              <InlineMarkdown content={parsedContent.content} />
            </NoteBox>
          </AiMessage>
        );
      case "updateChapter": {
        const notes = parsedContent.notes ?? [];
        const introText = parsedContent.chapterName
          ? `Your **${parsedContent.chapterName}** chapter is ready. Here's what changed:`
          : null;
        return (
          <AiMessage className="w-full">
            {introText && <MessageMarkdown content={introText} />}
            <StepList>
              {notes.length > 0 ? (
                notes.map((note, index) => (
                  <ProgressStep key={index} status="done">
                    <StepNoteMarkdown content={note} />
                  </ProgressStep>
                ))
              ) : (
                <ProgressStep status="done">
                  <StepNoteMarkdown content="Chapter details were updated." />
                </ProgressStep>
              )}
            </StepList>
          </AiMessage>
        );
      }
      case "answerAndUpdate": {
        const notes = parsedContent.notes ?? [];
        const introText = parsedContent.chapterName
          ? `Here's what changed in your **${parsedContent.chapterName}** chapter:`
          : "Here's what changed:";
        return (
          <AiMessage className="w-full">
            {parsedContent.content && <MessageMarkdown content={parsedContent.content} />}
            <MessageMarkdown content={introText} />
            <StepList>
              {notes.length > 0 ? (
                notes.map((note, index) => (
                  <ProgressStep key={index} status="done">
                    <StepNoteMarkdown content={note} />
                  </ProgressStep>
                ))
              ) : (
                <ProgressStep status="done">
                  <StepNoteMarkdown content="Chapter details were updated." />
                </ProgressStep>
              )}
            </StepList>
          </AiMessage>
        );
      }
      case "agent": {
        const notes = parsedContent.notes ?? [];
        return (
          <AiMessage className="w-full">
            <StatusRow icon={<SparkleIcon className="text-orange-500" />}>Chapter was generated by Agent</StatusRow>
            {parsedContent.content && <MessageMarkdown content={parsedContent.content} />}
            {notes.length > 0 && (
              <StepList>
                {notes.map((note, index) => (
                  <ProgressStep key={index} status="done">
                    <StepNoteMarkdown content={note} />
                  </ProgressStep>
                ))}
              </StepList>
            )}
          </AiMessage>
        );
      }
      case "prereqSummary":
        return (
          <AiMessage className="w-full">
            <PrereqChapterList variant="checkmarks" chapters={parsedContent.chapters ?? []} />
          </AiMessage>
        );
      case "answer":
      default:
        return (
          <AiMessage className="w-full">
            <MessageMarkdown content={parsedContent.content} />
          </AiMessage>
        );
    }
  };

  return <div className={`${styles.animatefadeIn} w-full`}>{renderByType()}</div>;
});
